import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../hooks/usePageMeta';
import { 
  ArrowRight, 
  Target, 
  Users, 
  Award, 
  Lightbulb,
  CheckCircle,
  TrendingUp,
  Shield
} from 'lucide-react';

const values = [
  {
    icon: Target,
    title: 'Results-Driven',
    description: 'Every strategy we create is focused on delivering measurable, tangible results for your business.',
  },
  {
    icon: Shield,
    title: 'Transparent',
    description: 'We believe in honest, transparent communication. You\'ll always know exactly what we\'re doing and why.',
  },
  {
    icon: Lightbulb,
    title: 'Innovative',
    description: 'We stay ahead of industry trends to bring you cutting-edge strategies that give you a competitive edge.',
  },
  {
    icon: Users,
    title: 'Collaborative',
    description: 'We work as an extension of your team, building partnerships based on trust and mutual success.',
  },
];

const stats = [
  { number: '6+', label: 'Years of Experience' },
  { number: '150+', label: 'Clients Served' },
  { number: '15+', label: 'Team Experts' },
  { number: '300+', label: 'Successful Projects' },
];

const whyUs = [
  'Data-driven strategies that deliver measurable ROI',
  'Industry-specific expertise across multiple verticals',
  'Dedicated account managers for personalized service',
  'Transparent reporting and communication',
  'Proven track record with documented case studies',
  'Agile approach that adapts to your business needs',
];

export const About = ({ onOpenAuditModal }) => {
  usePageMeta();
  
  return (
    <main className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
                About Us
              </p>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#1A1A1A] mb-6">
                We Help Businesses Grow Through Digital Marketing
              </h1>
              <p className="text-base md:text-lg text-[#525252] mb-8">
                Digital Startup is a results-driven digital marketing agency with over 6 years 
                of experience helping businesses achieve sustainable growth. We combine data-driven 
                strategies with creative execution to deliver measurable results.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={onOpenAuditModal}
                  className="bg-[#FFAA17] hover:bg-[#E69500] text-white rounded-full px-6"
                  data-testid="about-cta-primary"
                >
                  Get Free Growth Audit
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-[#E5E5E5] text-[#1A1A1A] rounded-full px-6"
                  data-testid="about-cta-secondary"
                >
                  <Link to="/work">View Our Work</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1631038506857-6c970dd9ba02?w=800"
                alt="Digital Startup Team"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-[#2E2E2E]" data-testid="about-stats">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-[#FFAA17]">{stat.number}</p>
                <p className="text-sm text-gray-400 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 md:py-24 bg-[#F7F7F7]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
              Our Story
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A] mb-6">
              Built on Results, Not Promises
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm">
              <p className="text-[#525252] mb-6 leading-relaxed">
                Digital Startup was founded with a simple belief: businesses deserve marketing 
                partners who deliver real, measurable results—not just vanity metrics. Our 
                founder, Bharat Jalora, saw too many businesses struggling with agencies that 
                promised the world but delivered little.
              </p>
              <p className="text-[#525252] mb-6 leading-relaxed">
                We took a different approach. We built a team of 15+ experts specializing in 
                SEO, performance marketing, and conversion optimization. Every strategy we 
                create is backed by data, every campaign is optimized for ROI, and every 
                client relationship is built on transparency.
              </p>
              <p className="text-[#525252] leading-relaxed">
                Today, we've helped over 150 businesses across healthcare, education, real estate, 
                and e-commerce achieve sustainable growth. Our results speak for themselves: 
                4000% revenue growth for e-commerce clients, 40% footfall increases for local 
                businesses, and conversion rates that consistently beat industry benchmarks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-white" data-testid="values-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
              Our Values
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]">
              What We Stand For
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-[#F7F7F7] rounded-2xl p-6 hover:shadow-md transition-shadow"
                  data-testid={`value-card-${index}`}
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm">
                    <Icon className="w-6 h-6 text-[#FFAA17]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">{value.title}</h3>
                  <p className="text-[#525252] text-sm">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-[#F7F7F7]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
                Why Choose Us
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A] mb-6">
                What Makes Digital Startup Different
              </h2>
              <p className="text-[#525252] mb-8">
                We're not just another digital marketing agency. We're growth partners who 
                are invested in your success. Here's what sets us apart:
              </p>
              <ul className="space-y-4">
                {whyUs.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#FFAA17] flex-shrink-0" />
                    <span className="text-[#525252]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-6 text-center">
                <TrendingUp className="w-10 h-10 text-[#FFAA17] mx-auto mb-3" />
                <p className="text-2xl font-bold text-[#1A1A1A]">4000%</p>
                <p className="text-sm text-[#525252]">Avg. Revenue Growth</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center">
                <Users className="w-10 h-10 text-[#FFAA17] mx-auto mb-3" />
                <p className="text-2xl font-bold text-[#1A1A1A]">150+</p>
                <p className="text-sm text-[#525252]">Happy Clients</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center">
                <Award className="w-10 h-10 text-[#FFAA17] mx-auto mb-3" />
                <p className="text-2xl font-bold text-[#1A1A1A]">6+</p>
                <p className="text-sm text-[#525252]">Years Experience</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center">
                <Target className="w-10 h-10 text-[#FFAA17] mx-auto mb-3" />
                <p className="text-2xl font-bold text-[#1A1A1A]">95%</p>
                <p className="text-sm text-[#525252]">Client Retention</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Message */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <div className="bg-[#2E2E2E] rounded-2xl p-8 md:p-12 text-white">
              <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
                From Our Founder
              </p>
              <h2 className="text-2xl md:text-3xl font-bold mb-6">
                "We believe every business deserves a growth partner who delivers real results."
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                When I started Digital Startup, I wanted to create an agency that businesses 
                could truly trust. One that doesn't hide behind jargon or vanity metrics, but 
                focuses on what matters: revenue growth, customer acquisition, and sustainable 
                business success.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Our commitment is simple—we win when you win. That's why we've built a team 
                of experts who are passionate about driving results, and why we continue to 
                invest in the latest tools and strategies to keep our clients ahead of the curve.
              </p>
              <div className="mt-8 flex items-center gap-4">
                <div className="w-14 h-14 bg-[#FFAA17] rounded-full flex items-center justify-center text-xl font-bold">
                  BJ
                </div>
                <div>
                  <p className="font-semibold">Bharat Jalora</p>
                  <p className="text-sm text-gray-400">Business Head, Digital Startup</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#FFAA17]" data-testid="about-cta-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Work With Us?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help grow your business. Get a free growth audit 
            and personalized strategy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={onOpenAuditModal}
              className="bg-white text-[#FFAA17] hover:bg-gray-100 rounded-full px-8 py-3"
              data-testid="about-final-cta"
            >
              Get Free Growth Audit
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-white text-white hover:bg-white/10 rounded-full px-8 py-3"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
